"use client"

import React from "react"
import { NotificationItemTemplate } from "./NotificationItemTemplate"
import { FragmentType, graphql, useFragment } from "@/gql"
import { NotificationActor } from "./NotificationActor"
import { Button, Text } from "@/elements"
import { useMutation } from "@apollo/client"
import {
  AcceptConnectionRequestDocument,
  RejectConnectionRequestDocument,
  UserNotificationFeedItem,
} from "@/gql/graphql"
import { ampli } from "@/ampli"
import type { NovaSession } from "@/types"

const CreativeWorkProjectLikeFragment = graphql(`
  fragment ReceivedConnectionRequest on StreamNotificationFeedAggregatedActivity {
    id
    isRead
    isSeen
    activities {
      id
      time
      actor {
        id
        name
        ...NotificationActor
      }
      foreignObject {
        __typename
        ... on ConnectionRequest {
          id
        }
      }
    }
  }
`)

interface Props {
  onActorClick: (e: React.MouseEvent<any, MouseEvent>) => void
  item: FragmentType<typeof CreativeWorkProjectLikeFragment>
  serverSession: NovaSession
}

export const ReceivedConnectionRequest: React.FC<Props> = props => {
  const item = useFragment(CreativeWorkProjectLikeFragment, props.item)
  const [loading, setLoading] = React.useState({
    accept: false,
    reject: false,
  })
  const { onActorClick } = props
  const activities = item.activities?.[0]
  const foreignObject = activities?.foreignObject
  const connectionRequestId = foreignObject?.__typename === "ConnectionRequest" ? foreignObject?.id : ""

  const [accept, { called: acceptCalled }] = useMutation(AcceptConnectionRequestDocument, {
    variables: {
      id: connectionRequestId!,
    },
    refetchQueries: "active",
    awaitRefetchQueries: true,
  })

  const [reject, { called: rejectCalled }] = useMutation(RejectConnectionRequestDocument, {
    variables: {
      id: connectionRequestId!,
    },
    refetchQueries: "active",
    awaitRefetchQueries: true,
  })

  const handleAccept = () => {
    if (loading.accept || loading.reject) return
    ampli.connectionRequestAccept({
      entry_point: "notification",
    })
    setLoading({ ...loading, accept: true })
    accept()
  }

  const handleIgnore = () => {
    if (loading.accept || loading.reject) return
    setLoading({ ...loading, reject: true })
    reject()
  }

  const actor = activities?.actor
  const actorName = actor?.name

  return (
    <NotificationItemTemplate
      isSeen={item?.isSeen}
      LeftComponent={actor && <NotificationActor actor={actor} onActorClick={onActorClick} />}
      TextContent={
        <Text element="span">
          <Text element="span" className="!font-medium" onClick={onActorClick}>
            {actorName ?? "Member"}
          </Text>{" "}
          wants to connect.
        </Text>
      }
      time={activities?.time}
      ActionsBar={
        connectionRequestId ? (
          <div className="mt-2 flex flex-row gap-2">
            <Button
              size="small"
              onClick={handleAccept}
              disabled={acceptCalled || rejectCalled || loading.reject}
              loading={loading.accept}>
              Accept
            </Button>
            <Button
              size="small"
              variant="secondary"
              onClick={handleIgnore}
              disabled={acceptCalled || rejectCalled || loading.accept}
              loading={loading.reject}>
              Ignore
            </Button>
          </div>
        ) : null
      }
    />
  )
}
